import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';

export default function UpdateEmail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to change your email');
      navigate('/login');
      return;
    }

    // Trim whitespace from email input
    const trimmedEmail = newEmail.trim();

    if (!trimmedEmail || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      
      // First verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update email in auth.users
      const { error: updateError } = await supabase.auth.updateUser({
        email: trimmedEmail
      });

      if (updateError) throw updateError;
      
      // Update email in public.users table
      const { error: profileError } = await supabase
        .from('users')
        .update({ email: trimmedEmail })
        .eq('id', user.id);

      if (profileError) throw profileError;

      toast.success('Email update initiated! Please check your new email inbox for a confirmation link.');
      navigate('/profile');
    } catch (error) {
      console.error('Error updating email:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update email');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-zinc-900 rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="h-6 w-6 text-primary-500" />
            <h1 className="text-2xl font-bold text-white">Update Email Address</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="current-email" className="block text-sm font-medium text-gray-300 mb-1">
                Current Email
              </label>
              <input
                type="email"
                id="current-email"
                value={user?.email || ''}
                disabled
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white opacity-70"
              />
            </div>

            <div>
              <label htmlFor="new-email" className="block text-sm font-medium text-gray-300 mb-1">
                New Email Address
              </label>
              <input
                type="email"
                id="new-email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Current Password (to verify)
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
              />
            </div>

            <div className="pt-4 flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Email'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}